// ---------------------------------------------------------------------------
// The nav's footer: the theme, Sign out, the legal line (R12d, R12e)
//
// One template for the SideNav's footer and for the drawer's (the MobileNav
// hands its footer to the SideNav inside it): a small Select "Theme", then a
// small, ghost, full-width Button "Sign out". It holds no state: the frame
// passes what to show and what a choice does (frame.ts), so both instances
// always show the same choice.
//
//   - Theme (R12e, Ferenc's decision of 30.10. on R12d's open point): the
//     system's Select (design/field.ts), size sm, labelled "Theme", with
//     System, Light, Dark, High contrast in words (I9), controlled. A choice
//     applies, saves and shows at once (frame.ts, on change); the keys are
//     the native select's, as the system's are. R12d had a SegmentedControl:
//     its four words need 252 px at size sm, the SideNav's footer has 199
//     (measured in Chrome), so it scrolled sideways in a strip of its own. A
//     Select is as wide as its column (the system's .sdr-select is width
//     100% in a field with min-width 0), so the footer never scrolls
//     sideways. The system's README keeps two to four short options for a
//     SegmentedControl; here the column decides.
//   - The Select's id is the caller's, one per nav (frame.ts): both navs draw
//     this template, and with one id a browser reads the drawer's label as
//     naming the first element with it, the side nav's Select, hidden on a
//     phone, so the drawer's would have no name.
//   - Sign out: the button, busy while the page's leave hooks run (frame.ts:
//     the question, then the hooks, then the sign-out). No icon, as in the
//     system's composition (icon-map.ts, sign-out).
//   - The legal line (d1): "© <year> New Digital Intelligence GmbH", then the
//     German "Datenschutzerklärung" and "Impressum" (lang="de", R3), each a
//     button opening the static dialog (legal-dialog.ts). The system has no
//     legal line for the console; this one takes AuthLayout's, the system's
//     legal line under content: its 12px/16px in ink-subtle, and its link,
//     sdr-auth__link, for the two buttons (the system's look and focus ring).
// Layout is Tailwind's, on a wrapper: the footer is a column, and the field
// is one of its items, stretched to its width.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { button } from '../design/button.js';
import { select } from '../design/field.js';
import type { ThemeChoice } from '../theme-choice.js';
import { THEME_OPTIONS } from './nav-model.js';

export type LegalText = 'privacy' | 'imprint';

export interface NavFooterInput {
  /** The id of this footer's theme Select: one per nav. */
  themeId: string;
  /** The choice both footers show. */
  theme: ThemeChoice;
  onTheme: (choice: ThemeChoice) => void;
  /** True while signing out waits for the page's leave hooks. */
  signingOut: boolean;
  onSignOut: (event: MouseEvent) => void;
  onLegal: (text: LegalText, event: MouseEvent) => void;
}

export function navFooter(p: NavFooterInput): TemplateResult {
  return html`<div class="flex flex-col gap-4">
    ${select({
      id: p.themeId,
      size: 'sm',
      label: 'Theme',
      options: [...THEME_OPTIONS],
      value: p.theme,
      onChange: (event: Event) => p.onTheme((event.currentTarget as HTMLSelectElement).value as ThemeChoice),
    })}
    ${button({ variant: 'ghost', size: 'sm', block: true, loading: p.signingOut, onClick: p.onSignOut }, 'Sign out')}
    <p class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-(--ink-subtle)">
      <span>© ${new Date().getFullYear()} New Digital Intelligence GmbH</span>
      <button type="button" class="sdr-auth__link" lang="de" @click=${(e: MouseEvent) => p.onLegal('privacy', e)}>
        Datenschutzerklärung
      </button>
      <button type="button" class="sdr-auth__link" lang="de" @click=${(e: MouseEvent) => p.onLegal('imprint', e)}>
        Impressum
      </button>
    </p>
  </div>`;
}
